import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import AppLayout from "@/components/AppLayout";
import { drawBossPortrait } from "@/components/game/riddleBoss";
import { Trophy, Lock, Crown, Sparkles } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Boss Gallery — trophy-room view of every pixel-art boss
 * riddle boss in the game. Defeated bosses are rendered in full color
 * with city-themed accessories; bosses you haven't beaten yet are dark
 * silhouettes with locked names. When all 20 are taken down the
 * GOLDEN GLOBETROTTER badge unlocks.
 */
export default function BossGallery() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/boss/progress`, {
          withCredentials: true,
        });
        if (!cancelled) setData(res.data);
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.detail || "Failed to load gallery");
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (error) {
    return (
      <AppLayout>
        <div className="p-12 text-center text-white/70" data-testid="boss-gallery-error">
          {error}
        </div>
      </AppLayout>
    );
  }
  if (!data) {
    return (
      <AppLayout>
        <div className="p-12 text-center text-white/40 text-[10px] tracking-[0.4em] animate-pulse">
          LOADING TROPHY ROOM…
        </div>
      </AppLayout>
    );
  }

  const { bosses, defeated_count, total, has_globetrotter, globetrotter_badge } = data;
  const pct = Math.round((defeated_count / total) * 100);

  return (
    <AppLayout>
      <div className="relative" data-testid="boss-gallery-page">
        {/* Header */}
        <div className="relative h-[260px] overflow-hidden border-b border-white/10 bg-gradient-to-br from-[#1a0e1f] via-[#0a0a0a] to-[#1a1408]">
          <SunburstRays count={defeated_count} total={total} />
          <div className="relative h-full flex items-end px-6 lg:px-12 pb-10">
            <div className="flex-1">
              <div className="label-kicker mb-2">Trophy Room</div>
              <h1 className="font-heading text-5xl lg:text-6xl flex items-center gap-3">
                Boss <span className="text-[#FFCC00]">Gallery</span>
              </h1>
              <p className="mt-2 text-sm text-white/50 max-w-xl">
                Every champ you&apos;ve put on the canvas. Beat all 20 to claim the{" "}
                <span className="text-[#FFCC00] font-mono">{globetrotter_badge}</span>.
              </p>
            </div>
            <div
              className="hidden md:flex flex-col items-end gap-2"
              data-testid="boss-gallery-stats"
            >
              <div className="font-heading text-6xl lg:text-7xl tracking-tight">
                <span className="text-[#FFCC00]">{defeated_count}</span>
                <span className="text-white/30">/{total}</span>
              </div>
              <div className="text-[10px] tracking-[0.3em] text-white/50 uppercase">
                Bosses Defeated
              </div>
            </div>
          </div>
        </div>

        {/* Progress bar */}
        <div className="px-6 lg:px-12 pt-8">
          <div className="surface-solid p-5 flex items-center gap-5" data-testid="boss-gallery-progress">
            <div className="flex-1">
              <div className="flex items-center justify-between mb-2">
                <span className="label-kicker">Globetrotter Progress</span>
                <span className="font-mono text-xs text-white/60">{pct}%</span>
              </div>
              <div className="h-3 bg-white/5 border border-white/10 overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-[#FF3B30] via-[#FFCC00] to-[#FFD700] transition-all duration-500"
                  style={{ width: `${pct}%` }}
                />
              </div>
            </div>
            <div
              className={`px-4 py-3 border-2 flex items-center gap-2 transition-all ${
                has_globetrotter
                  ? "border-[#FFCC00] bg-[#FFCC00]/15 text-[#FFCC00] shadow-[0_0_22px_rgba(255,204,0,0.45)]"
                  : "border-white/10 text-white/30"
              }`}
              data-testid={`globetrotter-badge-${has_globetrotter ? "earned" : "locked"}`}
            >
              {has_globetrotter ? <Crown className="w-5 h-5" /> : <Lock className="w-5 h-5" />}
              <div className="leading-tight">
                <div className="text-[9px] tracking-[0.3em] uppercase opacity-70">
                  {has_globetrotter ? "Earned" : "Locked"}
                </div>
                <div className="font-heading text-base">{globetrotter_badge}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Grid */}
        <div className="px-6 lg:px-12 py-10">
          <div
            className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 lg:gap-4"
            data-testid="boss-gallery-grid"
          >
            {bosses.map((b) => (
              <BossCard key={`${b.city_id}-${b.boss_index}`} boss={b} />
            ))}
          </div>

          {has_globetrotter && (
            <div
              className="mt-12 surface-solid border-2 border-[#FFCC00] p-8 text-center bg-[#FFCC00]/5"
              data-testid="globetrotter-unlocked-banner"
            >
              <Sparkles className="w-8 h-8 text-[#FFCC00] mx-auto mb-3" />
              <div className="font-heading text-3xl text-[#FFCC00]">
                GOLDEN GLOBETROTTER UNLOCKED
              </div>
              <p className="text-sm text-white/60 mt-2">
                You&apos;ve clobbered every champ on every continent. The world is yours, racer.
              </p>
            </div>
          )}
        </div>
      </div>
    </AppLayout>
  );
}

// ---------------------------------------------------------------------------
// Single boss trophy card. Renders a small canvas that paints the boss
// portrait via drawBossPortrait, then animates a subtle bob.
// ---------------------------------------------------------------------------
function BossCard({ boss }) {
  const canvasRef = useRef(null);
  const rafRef = useRef(null);

  useEffect(() => {
    const cvs = canvasRef.current;
    if (!cvs) return;
    const ctx = cvs.getContext("2d");
    // Hi-DPI sharpening
    const dpr = window.devicePixelRatio || 1;
    const cssW = 200;
    const cssH = 200;
    cvs.width = Math.floor(cssW * dpr);
    cvs.height = Math.floor(cssH * dpr);
    cvs.style.width = `${cssW}px`;
    cvs.style.height = `${cssH}px`;
    ctx.scale(dpr, dpr);
    ctx.imageSmoothingEnabled = false;

    const render = () => {
      ctx.clearRect(0, 0, cssW, cssH);
      // Paint the boss centered
      drawBossPortrait(ctx, boss.city_id, boss.boss_index, cssW / 2, cssH / 2 + 30, {
        showName: false,
        showShadow: true,
        silhouette: !boss.defeated,
        scale: 1.05,
      });
      rafRef.current = requestAnimationFrame(render);
    };
    render();
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [boss.city_id, boss.boss_index, boss.defeated]);

  const cardTestId = `boss-card-${boss.city_id}-${boss.boss_index}`;
  return (
    <div
      className={`relative border-2 transition-all duration-200 group overflow-hidden ${
        boss.defeated
          ? "border-[#FFCC00]/50 bg-[#FFCC00]/[0.03] hover:border-[#FFCC00] hover:bg-[#FFCC00]/[0.07] hover:-translate-y-0.5"
          : "border-white/10 bg-black/40 hover:border-white/20"
      }`}
      data-testid={cardTestId}
    >
      {/* Trophy ribbon for defeated */}
      {boss.defeated && (
        <div className="absolute top-2 right-2 z-10 flex items-center gap-1 bg-[#FFCC00] text-black text-[9px] font-bold tracking-widest px-2 py-1">
          <Trophy className="w-3 h-3" /> KO
        </div>
      )}
      {/* Lock for undefeated */}
      {!boss.defeated && (
        <div className="absolute top-2 right-2 z-10 text-white/30">
          <Lock className="w-3.5 h-3.5" />
        </div>
      )}

      {/* Stage backdrop — subtle ring spotlight */}
      <div
        className={`relative h-[200px] flex items-center justify-center ${
          boss.defeated
            ? "bg-[radial-gradient(circle_at_center,rgba(255,204,0,0.15),transparent_65%)]"
            : "bg-[radial-gradient(circle_at_center,rgba(80,80,100,0.1),transparent_65%)]"
        }`}
      >
        {boss.defeated && (
          <div className="absolute inset-0 pointer-events-none opacity-40 group-hover:opacity-60 transition-opacity">
            <Rays />
          </div>
        )}
        <canvas ref={canvasRef} className="relative" data-testid={`${cardTestId}-canvas`} />
      </div>

      {/* Footer with boss + city name */}
      <div className="border-t border-white/10 px-3 py-3 bg-black/40">
        <div
          className={`font-heading text-base leading-tight uppercase ${
            boss.defeated ? "text-[#FFCC00]" : "text-white/30"
          }`}
          data-testid={`${cardTestId}-name`}
        >
          {boss.defeated ? boss.boss_name : "??? ??? ???"}
        </div>
        <div className="text-[10px] tracking-widest uppercase text-white/50 mt-1 flex items-center justify-between">
          <span>{boss.city_name}</span>
          <span className="font-mono text-white/30">RT {boss.boss_index + 1}</span>
        </div>
      </div>
    </div>
  );
}

// Simple decorative animated rays SVG behind defeated bosses
function Rays() {
  return (
    <svg viewBox="0 0 200 200" width="100%" height="100%">
      <g style={{ transformOrigin: "100px 100px", animation: "raysSpin 14s linear infinite" }}>
        {Array.from({ length: 12 }).map((_, i) => {
          const a = (i / 12) * Math.PI * 2;
          const x2 = 100 + Math.cos(a) * 140;
          const y2 = 100 + Math.sin(a) * 140;
          return (
            <line
              key={i}
              x1="100"
              y1="100"
              x2={x2}
              y2={y2}
              stroke="#FFCC00"
              strokeWidth={i % 2 === 0 ? 6 : 3}
              opacity="0.35"
            />
          );
        })}
      </g>
    </svg>
  );
}

// Hero sunburst — count of defeated bosses radiates gold rays from the
// top-right corner. Empty state = subtle outline only.
function SunburstRays({ count, total }) {
  const ratio = total > 0 ? count / total : 0;
  const intensity = 0.15 + ratio * 0.55;
  return (
    <svg
      viewBox="0 0 800 300"
      width="100%"
      height="100%"
      className="absolute inset-0 pointer-events-none"
      preserveAspectRatio="xMaxYMin slice"
    >
      <g
        style={{
          transformOrigin: "640px 80px",
          animation: "raysSpin 60s linear infinite",
        }}
      >
        {Array.from({ length: 24 }).map((_, i) => {
          const a = (i / 24) * Math.PI * 2;
          const x2 = 640 + Math.cos(a) * 900;
          const y2 = 80 + Math.sin(a) * 900;
          return (
            <line
              key={i}
              x1="640"
              y1="80"
              x2={x2}
              y2={y2}
              stroke={i % 2 === 0 ? "#FFCC00" : "#FF3B30"}
              strokeWidth={i % 3 === 0 ? 22 : 10}
              opacity={i % 2 === 0 ? intensity : intensity * 0.6}
            />
          );
        })}
      </g>
      <circle cx="640" cy="80" r="56" fill="#0A0A0A" />
      <circle cx="640" cy="80" r="56" fill="none" stroke="#FFCC00" strokeWidth="2" opacity={0.3 + ratio * 0.5} />
    </svg>
  );
}
